import { Link, Tooltip, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { usePlanId } from "components/common/boundary/PlanId";
import { MixDetailsPanel } from "components/common/panels/mixDetails/MixDetailsPanel";
import { LoadedProductGroupPanel } from "components/common/panels/productGroup";
import { useTenantTranslation } from "hooks/formatters";
import { useHeatTextFormatter } from "hooks/heats";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { mapLoadedUnpack } from "models/loaded";
import React, { useMemo } from "react";
import { Group, useIsStatic, usePeriodOption } from "./ProductionContext";
import { useLayering } from "hooks/useLayering";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useStringQueryParamState } from "hooks/useStringQueryParamState";

interface BaseProps {
  heats: number;
  name: string;
  multipleMixes: boolean;
  mixNumber: number;
  groupId: number;
  groupOption: Group;
  customerMixId: string | null;
  headerType: "row" | "column";
}

type LayeringTableTypeProps = {
  chemistryGroupId: number;
  period: number;
} & BaseProps;

type BasketPlotTypeProps = {
  numBaskets: number;
} & BaseProps;

interface BaseHeatHeaderProps {
  heats: number;
  name: string;
  multipleMixes: boolean;
  mixNumber: number;
  groupId: number;
  groupOption: Group;
  numBaskets: number | null;
  customerMixId: string | null;
  headerType: "row" | "column";
}

type Props = LayeringTableTypeProps | BasketPlotTypeProps;

export const HeatHeader = ({
  heats,
  name,
  multipleMixes,
  mixNumber,
  groupId,
  groupOption,
  customerMixId,
  headerType,
  ...props
}: Props) => {
  if ("chemistryGroupId" in props) {
    return (
      <LayeringTableTypeHeatHeader
        heats={heats}
        period={props.period}
        name={name}
        multipleMixes={multipleMixes}
        mixNumber={mixNumber}
        groupId={groupId}
        groupOption={groupOption}
        customerMixId={customerMixId}
        headerType={headerType}
        chemistryGroupId={props.chemistryGroupId}
      />
    );
  } else {
    return (
      <BasketPlotTypeHeatHeader
        heats={heats}
        name={name}
        multipleMixes={multipleMixes}
        mixNumber={mixNumber}
        groupId={groupId}
        groupOption={groupOption}
        customerMixId={customerMixId}
        headerType={headerType}
        numBaskets={props.numBaskets}
      />
    );
  }
};

export const BaseHeatHeader = ({
  heats,
  name,
  multipleMixes,
  mixNumber,
  groupId,
  groupOption,
  numBaskets,
  customerMixId,
  headerType,
}: BaseHeatHeaderProps) => {
  const theme = useTheme();
  const { t } = useTenantTranslation();
  const { format } = useNumberSerialiser();
  const planId = usePlanId();
  const period = usePeriodOption();
  const isStatic = useIsStatic();

  const [isModalOpen, setIsModalOpen] = useStringQueryParamState<string | null>(
    "selected_mix_id",
    null
  );

  const heatTextFormatter = useHeatTextFormatter();
  const heatText = `${format(heats)} ${heats === 1 ? t("heat") : t("heats")}`;
  const text = heatTextFormatter(multipleMixes, name, mixNumber);
  const basketText = React.useMemo(() => {
    if (numBaskets !== null) {
      return `${numBaskets} ${numBaskets === 1 ? t("basket") : t("baskets")}`;
    } else {
      return null;
    }
  }, [numBaskets, t]);

  const headerTypeSx = headerType === "column" ? { height: "100%" } : {};

  return (
    <>
      <Tooltip
        title={`${heatText}${
          basketText !== null ? ` - ${basketText}` : ""
        } - ${text}${customerMixId ? `- ${customerMixId}` : ""}`}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            ...headerTypeSx,
          }}
          flexDirection="column"
          justifyContent="flex-start"
          display="flex"
        >
          <Box display="flex" alignItems="center">
            <Typography
              variant="body2"
              sx={{
                fontWeight: 400,
                whiteSpace: "nowrap",
              }}
            >
              {heatText} {basketText !== null ? `• ${basketText}` : ""}
            </Typography>
            {customerMixId ? (
              <CheckCircleIcon
                fontSize="small"
                sx={{ color: "green", ml: 0.5 }}
              />
            ) : null}
          </Box>
          {isStatic ? (
            <Typography
              variant="body2"
              color={theme.palette.text.primary}
              fontWeight="bold"
              sx={{ textWrap: "wrap", width: "100%" }}
            >
              {text}
            </Typography>
          ) : (
            <Link
              variant="body2"
              color={theme.palette.text.primary}
              fontWeight="bold"
              sx={{ cursor: "pointer", textWrap: "wrap", width: "100%" }}
              onClick={() => {
                setIsModalOpen(groupId.toString());
              }}
            >
              {text}
            </Link>
          )}
          {customerMixId ? (
            <Typography
              variant="caption"
              sx={{
                color: "green",
                fontSize: "0.8rem",
              }}
            >
              {customerMixId}
            </Typography>
          ) : null}
        </Box>
      </Tooltip>
      {isStatic ? null : groupOption === Group.Chef ? (
        <MixDetailsPanel
          open={isModalOpen === groupId.toString()}
          setOpen={(open) => {
            setIsModalOpen(open ? groupId.toString() : null);
          }}
          group={groupId}
          mix={mixNumber}
          multipleMixes={multipleMixes}
          planId={planId}
          period={period}
        />
      ) : (
        <LoadedProductGroupPanel
          id={groupId}
          open={isModalOpen === groupId.toString()}
          doClose={() => {
            setIsModalOpen(null);
          }}
        />
      )}
    </>
  );
};

const LayeringTableTypeHeatHeader = ({
  heats,
  name,
  multipleMixes,
  mixNumber,
  groupId,
  groupOption,
  chemistryGroupId,
  customerMixId,
  headerType,
  period,
}: LayeringTableTypeProps) => {
  const planId = usePlanId();

  const key = useMemo(
    () => ({
      planId,
      groupId: chemistryGroupId,
      mixNumber,
      period,
    }),
    [planId, chemistryGroupId, mixNumber, period]
  );

  const layeringSummary = mapLoadedUnpack(
    useLayering(key),
    (layering) => layering.summary
  );

  const numBaskets = useMemo(() => {
    return layeringSummary?.charges
      ? Object.values(layeringSummary.charges).reduce(
          (total, layerSumary) => total + (layerSumary.length > 0 ? 1 : 0),
          0
        )
      : 0;
  }, [layeringSummary]);

  return (
    <BaseHeatHeader
      heats={heats}
      name={name}
      multipleMixes={multipleMixes}
      mixNumber={mixNumber}
      groupId={groupId}
      groupOption={groupOption}
      numBaskets={numBaskets}
      customerMixId={customerMixId}
      headerType={headerType}
    />
  );
};

const BasketPlotTypeHeatHeader = ({
  heats,
  name,
  multipleMixes,
  mixNumber,
  groupId,
  groupOption,
  numBaskets,
  customerMixId,
  headerType,
}: BasketPlotTypeProps) => {
  return (
    <BaseHeatHeader
      heats={heats}
      name={name}
      multipleMixes={multipleMixes}
      mixNumber={mixNumber}
      groupId={groupId}
      groupOption={groupOption}
      numBaskets={numBaskets}
      customerMixId={customerMixId}
      headerType={headerType}
    />
  );
};
